import React, { useState, useEffect } from "react";
import { newslettersApi } from "../utils/api";
import { toast } from "sonner"; // Assuming sonner is used for toasts, based on App.tsx
import { Eye, PencilLine } from "lucide-react";

type CreateNewsletterModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreate: () => void;
};

const CreateNewsletterModal: React.FC<CreateNewsletterModalProps> = ({
  isOpen,
  onClose,
  onCreate,
}) => {
  const [form, setForm] = useState({ title: "", subject: "", content: "" });
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"edit" | "preview">("edit");

  useEffect(() => {
    if (isOpen) {
      setForm({ title: "", subject: "", content: "" });
      setMode("edit");
    }
  }, [isOpen]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreate = async () => {
    if (!form.title || !form.subject || !form.content) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      const response = await newslettersApi.createNewsletter(form);
      if (response && response.statusCode === 200) {
        toast.success("Newsletter created successfully");
        onCreate();
        onClose();
      } else {
        toast.error(response?.message || "Failed to create newsletter");
      }
    } catch (error: any) {
      toast.error(error?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const previewSrcDoc = `
    <!doctype html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style>
          :root {
            color-scheme: light;
          }
          * {
            box-sizing: border-box;
          }
          body {
            margin: 0;
            padding: 24px;
            background:
              radial-gradient(circle at top left, rgba(59, 130, 246, 0.12), transparent 35%),
              radial-gradient(circle at bottom right, rgba(16, 185, 129, 0.10), transparent 32%),
              #f8fafc;
            font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
            color: #0f172a;
          }
          .shell {
            max-width: 640px;
            margin: 0 auto;
          }
          .card {
            background: #ffffff;
            border: 1px solid rgba(148, 163, 184, 0.35);
            border-radius: 20px;
            box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
            overflow: hidden;
          }
          .hero {
            padding: 24px 24px 20px;
            background: linear-gradient(135deg, rgba(15, 23, 42, 0.03), rgba(59, 130, 246, 0.08));
            border-bottom: 1px solid rgba(148, 163, 184, 0.25);
          }
          .eyebrow {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: #2563eb;
            margin-bottom: 12px;
          }
          .title {
            margin: 0 0 8px;
            font-size: 28px;
            line-height: 1.15;
            letter-spacing: -0.03em;
          }
          .subject {
            margin: 0;
            color: #475569;
            font-size: 15px;
            line-height: 1.6;
          }
          .content {
            padding: 24px;
            background: #fff;
          }
          .content :where(img) {
            max-width: 100%;
            height: auto;
            display: block;
          }
          .content :where(table) {
            width: 100%;
            border-collapse: collapse;
          }
          .content :where(a) {
            color: #2563eb;
          }
          .content :where(h1, h2, h3, h4, h5, h6, p, ul, ol, blockquote) {
            margin-top: 0;
          }
          .content :where(p, li, blockquote) {
            color: #0f172a;
            line-height: 1.7;
          }
          .empty {
            padding: 48px 24px;
            text-align: center;
            color: #64748b;
          }
          .empty strong {
            display: block;
            margin-bottom: 8px;
            color: #0f172a;
          }
        </style>
      </head>
      <body>
        <div class="shell">
          <div class="card">
            <div class="hero">
              <div class="eyebrow">
                Newsletter Preview
              </div>
              <h1 class="title">${form.title || "Your newsletter title"}</h1>
              <p class="subject">${form.subject || "Your subject line will appear here."}</p>
            </div>
            <div class="content">
              ${
                form.content
                  ? form.content
                  : '<div class="empty"><strong>No content yet</strong>Start typing HTML to see a live preview.</div>'
              }
            </div>
          </div>
        </div>
      </body>
    </html>
  `;

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[100] bg-black bg-opacity-50" onClick={onClose}></div>
      <div className="fixed inset-0 z-[101] flex items-start justify-center overflow-y-auto p-2 sm:p-4 pointer-events-none">
        <div className="bg-background rounded-2xl shadow-xl w-full max-w-4xl max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-1.5rem)] flex flex-col pointer-events-auto border overflow-hidden">
          <div className="flex-none p-4 sm:p-5 border-b border-border">
            <h2 className="text-lg sm:text-xl font-bold">Create Newsletter</h2>
          </div>
          
          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Title:</label>
              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="e.g. April Newsletter"
                className="w-full flex h-10 rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Subject:</label>
              <input
                name="subject"
                value={form.subject}
                onChange={handleChange}
                placeholder="e.g. Latest Updates 🚀"
                className="w-full flex h-10 rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <label className="text-sm font-medium">Content (HTML):</label>
                  <div className="inline-flex self-start rounded-lg border border-border bg-muted/30 p-1 shadow-sm">
                  <button
                    type="button"
                    onClick={() => setMode("edit")}
                    className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      mode === "edit"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <PencilLine className="h-4 w-4" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("preview")}
                    className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      mode === "preview"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Eye className="h-4 w-4" />
                    Preview
                  </button>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div
                  className={`space-y-2 ${
                    mode === "preview" ? "hidden lg:block" : "block"
                  }`}
                >
                  <textarea
                    name="content"
                    value={form.content}
                    onChange={handleChange}
                    placeholder="<html><body><h1>Hello Users</h1><p>We have some exciting news!</p></body></html>"
                    className="w-full min-h-[180px] sm:min-h-[220px] lg:min-h-[260px] flex rounded-xl border border-border bg-background px-3 py-3 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
                  />
                  <p className="text-xs text-muted-foreground">
                    HTML renders live in preview. Scripts are disabled for safety.
                  </p>
                </div>

                <div
                  className={`rounded-xl border border-border bg-slate-50 shadow-sm overflow-hidden ${
                    mode === "edit" ? "hidden lg:block" : "block"
                  }`}
                >
                  <div className="flex items-center justify-between border-b border-border bg-white px-4 py-3">
                    <div>
                      <div className="text-sm font-semibold">Live Preview</div>
                      <div className="text-xs text-muted-foreground">
                        Email rendering inside a sandboxed frame
                      </div>
                    </div>
                    <span className="rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-semibold text-emerald-700">
                      Preview
                    </span>
                  </div>
                  <iframe
                    title="Newsletter preview"
                    srcDoc={previewSrcDoc}
                    sandbox=""
                    className="h-[240px] sm:h-[280px] lg:h-[320px] w-full bg-slate-50"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex-none p-3 sm:p-4 border-t border-border flex flex-col-reverse gap-3 sm:flex-row sm:justify-end bg-muted/20">
            <button 
              onClick={onClose} 
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
            >
              Cancel
            </button>
            <button 
              onClick={handleCreate} 
              disabled={loading} 
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
            >
              {loading ? "Creating..." : "Create Newsletter"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateNewsletterModal;
